import { memo, useEffect, useRef, useState } from "react"

import { Loader } from "../Loader"

const FADE_DURATION = 300
const LOADING_INDICATOR_THRESHOLD = 1000
/**
 * The splash removes itself this long after mounting, whether or not it was
 * turned off. `Boot` shows its auth-unavailable state at the same moment, so the
 * page is never left empty once the splash is gone.
 */
export const SPLASH_SAFEGUARD_MS = 10000

const SplashScreen = memo(({ turnOff = false }: { turnOff: boolean }) => {
  const [fade, setFade] = useState(false)
  const [visible, setVisible] = useState(true)
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false)
  const [loadingIndicatorOpacity, setLoadingIndicatorOpacity] = useState(0)
  const loadingIndicatorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const turnOffRef = useRef(turnOff)

  // Keep ref in sync with prop
  useEffect(() => {
    turnOffRef.current = turnOff
  }, [turnOff])

  // Show loading indicator only if loading takes more than 1 second
  useEffect(() => {
    loadingIndicatorTimeoutRef.current = setTimeout(() => {
      if (!turnOffRef.current) {
        setShowLoadingIndicator(true)
        // Trigger fade-in animation
        setTimeout(() => setLoadingIndicatorOpacity(1), 0)
      }
    }, LOADING_INDICATOR_THRESHOLD)

    return () => {
      if (loadingIndicatorTimeoutRef.current) {
        clearTimeout(loadingIndicatorTimeoutRef.current)
      }
    }
  }, [])

  // Fade out immediately when app is ready
  useEffect(() => {
    if (turnOff) {
      if (loadingIndicatorTimeoutRef.current) {
        clearTimeout(loadingIndicatorTimeoutRef.current)
      }
      setFade(true)
    }
  }, [turnOff])

  // Safeguard: auto-fade after 10s
  useEffect(() => {
    const safeguard = setTimeout(() => {
      setFade(true)
    }, SPLASH_SAFEGUARD_MS)
    return () => clearTimeout(safeguard)
  }, [])

  // After fade, remove from DOM
  useEffect(() => {
    if (fade) {
      const t = setTimeout(() => setVisible(false), FADE_DURATION)
      return () => clearTimeout(t)
    }
    return undefined
  }, [fade])

  if (!visible) return null

  return (
    <div
      className="bg-nx-surface-bg-base fixed inset-0 flex h-screen w-screen items-center justify-center"
      style={{
        opacity: fade ? 0 : 1,
        transition: `opacity ${FADE_DURATION}ms ease-out`
      }}
    >
      {showLoadingIndicator && (
        <div
          className="h-6 w-6"
          style={{
            opacity: loadingIndicatorOpacity,
            transition: "opacity 200ms ease-in"
          }}
        >
          <Loader className="h-full w-full" />
        </div>
      )}
    </div>
  )
})

export default SplashScreen
